import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Pencil, Plus } from 'lucide-react';
import { useState, type ChangeEvent, type FC } from 'react';

type CreateCriteriaModalProps = {
  mode: 'create';
  onCreate: (name: string, instructions: string) => void;
};

type EditCriteriaModalProps = {
  mode: 'edit';
  id: string;
  name: string;
  instructions: string;
  onEdit: (id: string, name: string, instructions: string) => void;
};

export const CriteriaModal: FC<
  CreateCriteriaModalProps | EditCriteriaModalProps
> = props => {
  const isEdit = props.mode === 'edit';
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState(isEdit ? props.name : '');
  const [instructions, setInstructions] = useState(
    isEdit ? props.instructions : ''
  );

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };
  const handleInstructionsChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInstructions(e.target.value);
  };

  const errorMessage = !name.trim()
    ? 'Name is required'
    : !instructions.trim()
      ? 'Instructions are required'
      : undefined;

  const resetInput = () => {
    setName('');
    setInstructions('');
  };

  const handleSubmit = () => {
    if (props.mode === 'create') {
      props.onCreate(name, instructions);
      resetInput();
    }
    if (props.mode === 'edit') {
      props.onEdit(props.id, name, instructions);
    }
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button variant="outline" size="icon">
            <Pencil />
          </Button>
        ) : (
          <Button variant="outline" className="w-fit">
            <Plus />
            Add an exclusion criterion
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit criteria' : 'Create criteria'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Edit the criteria for literature screening.'
              : 'Create a new criteria for literature screening.'}
          </DialogDescription>
        </DialogHeader>
        <FlexCol gap="4">
          <FlexCol gap="2">
            <Input
              label="Name"
              value={name}
              placeholder="Published in 2025"
              required
              onChange={handleNameChange}
            />
          </FlexCol>
          <FlexCol gap="2">
            <Textarea
              label="Instructions"
              required
              rows={4}
              value={instructions}
              placeholder="Check if the article was published in 2025"
              onChange={handleInstructionsChange}
            />
          </FlexCol>
        </FlexCol>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button onClick={handleSubmit} disabled={!!errorMessage}>
                  {isEdit ? 'Edit' : 'Create'}
                </Button>
              </div>
            </TooltipTrigger>
            {!!errorMessage && <TooltipContent>{errorMessage}</TooltipContent>}
          </Tooltip>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
